var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { forEachConfig } from './framework/DspConfig';
const inputTypes = ['headphoneInput', 'opticalInput', 'rcaInput'];
const outputType = ['audioOutput'];
const allTypes = [...inputTypes, ...outputType];
const allowedTypes = {
    mics: outputType,
    micsMuteStatus: [],
    amplifier: outputType,
    assistiveListeningMute: allTypes,
    assistiveListeningMuteStatus: [],
    assistiveListeningVolume: outputType,
    audioInputMute: inputTypes,
    audioInputMuteStatus: [],
    audioInputVolume: [],
    overflowRoomMute: allTypes,
    overflowRoomMuteStatus: [],
    overflowRoomVolume: outputType,
    soundReinforcementMute: allTypes,
    soundReinforcementMuteStatus: [],
    soundReinforcementVolume: outputType,
    telephony: [],
    receiveGain: [],
    preset: [],
};
export class AlfatronAmp {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = {
            input1: { channelNumber: 1, name: 'Input 1', type: 'rcaInput' },
            input2: { channelNumber: 2, name: 'Input 2', type: 'headphoneInput' },
            input3: { channelNumber: 3, name: 'Input 3', type: 'opticalInput' },
            output: { channelNumber: 4, name: 'Output', type: 'audioOutput' },
        };
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: '600%',
                succeed: /A\s*:\s*\d+\s*->\s*\d|Please Input Your Command/,
            },
        ];
        this.isAudioInputSource = {};
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.makeDetectedResults();
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                return this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                // Output Mute:
                if (key === 'output') {
                    const match = yield this.socket.send(`0A${value ? '0' : '1'}.`, /^(Mute|Unmute)/i, tags);
                    return match[1].toLowerCase() === 'mute';
                }
                // Input Mute:
                //
                // The PA100W does not have input mutes, but it does have an input selector. This is
                // meant to be used by the integrator to select the input channel for the install and
                // then not controlled by the user. However, we think some people could want to use it
                // as a way to do simple muting or select between audio input sources on Inara, which
                // are mutually exclusive. So, when an input is unmuted, we select it and update the
                // mute states of the other inputs to true as a side effect.
                if (!value) {
                    // Select input and assume it's successful (if not, polling will correct the state).
                    yield this.socket.send(`${channel.channelNumber}A1.`, /A\s*:\s*(\d+)\s*->\s*\d/i, tags);
                    // Update mute states to true for other inputs.
                    for (const [otherKey, otherChannel] of Object.entries(this.channels)) {
                        if (otherChannel.type.endsWith('Input') &&
                            otherKey in this.state.mutes.values) {
                            this.state.mutes.update(otherKey, true);
                        }
                    }
                    return value;
                }
                else {
                    if (this.isAudioInputSource[key]) {
                        // Audio input sources are implemented as mutually exclusive in Inara. When
                        // Inara unmutes one, it mutes the others. We assume we've done everything we
                        // needed to do when Inara unmuted the appropriate input, so we don't take any
                        // action for the mute case.
                        return this.state.mutes.values[key];
                    }
                    else {
                        // For other types of mutes, we unmute the next input when this input is muted.
                        // Hopefully that's what the integrator wanted.
                        let nextKey = 'input1';
                        for (const [otherKey, otherChannel] of Object.entries(this.channels)) {
                            if (otherChannel.type.endsWith('Input') &&
                                otherChannel.channelNumber === channel.channelNumber + 1) {
                                nextKey = otherKey;
                                break;
                            }
                        }
                        const nextChannel = this.channels[nextKey];
                        if (nextChannel === undefined)
                            throw new Error(`no channel for key ${nextKey}`);
                        // Select the next input and assume it's successful (if not, polling will
                        // correct the state).
                        yield this.socket.send(`${nextChannel.channelNumber}A1.`, /A\s*:\s*(\d+)\s*->\s*\d/i, tags);
                        // Update mute states for other inputs.
                        for (const [otherKey, otherChannel] of Object.entries(this.channels)) {
                            if (otherChannel.channelNumber !== channel.channelNumber &&
                                otherChannel.type.endsWith('Input') &&
                                otherKey in this.state.mutes.values) {
                                this.state.mutes.update(otherKey, otherChannel.channelNumber !== nextChannel.channelNumber);
                            }
                        }
                        return value;
                    }
                }
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    name: 'inputSelection',
                    regExp: /A\s*:\s*(\d+)\s*->\s*\d/i,
                    callback: (match) => {
                        const channelNumber = parseInt(match[1], 10);
                        for (const [key, channel] of Object.entries(this.channels)) {
                            if (channel.type.endsWith('Input') && key in this.state.mutes.values) {
                                this.state.mutes.update(key, channel.channelNumber !== channelNumber);
                            }
                        }
                    },
                },
                {
                    name: 'volumeMute',
                    regExp: /^(Mute|Unmute)/i,
                    callback: (match) => {
                        const value = match[1].toLowerCase() === 'mute';
                        this.state.mutes.update('output', value);
                    },
                },
            ],
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                return this.state.volumes.values[key];
            }),
            set: (key, volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`7${volume.toString().padStart(2, '0')}%`, /Volume\s+of\s+LINE\s*:\s*(\d+)/i, tags);
                return parseInt(match[1], 10);
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 60, min: 36, round: 1 };
                return { max: 60, min: 0, round: 1 };
            }),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    name: 'volumeLevel',
                    regExp: /Volume\s+of\s+LINE\s*:\s*(\d+)/i,
                    callback: (match) => {
                        const value = parseInt(match[1], 10);
                        this.state.volumes.update('output', value);
                    },
                },
            ],
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.socket.sendAsync(`600%`, ['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            // Because we're implementing the input mutes as mutually exclusive via the input selection
            // command, we have to do a lot more housekeeping than the typical driver. Here, we are
            // examining the driver's config for two reasons. First, we need to determine which mute
            // control points are for audio input sources so we can handle them differently in the mute
            // set implementation. Second, we need to do an initial query of the "mute" and volume and
            // only update the states for which controls are configured.
            const receivedResponses = {};
            const markResponseReceived = {};
            this.isAudioInputSource = {};
            forEachConfig(config, (path, type, cfg) => {
                if (type === 'mute') {
                    for (const [key, channel] of Object.entries(this.channels)) {
                        if (channel.channelNumber === cfg) {
                            if (path[0] === 'audioInputSources') {
                                this.isAudioInputSource[key] = true;
                            }
                            if (key === 'output') {
                                if (!receivedResponses.volumeMute) {
                                    receivedResponses.volumeMute = new Promise(resolve => (markResponseReceived.volumeMute = resolve));
                                }
                            }
                            else {
                                receivedResponses[key] = new Promise(resolve => (markResponseReceived[key] = resolve));
                            }
                            break;
                        }
                    }
                }
                else if (type === 'volume') {
                    receivedResponses.volume = new Promise(resolve => (markResponseReceived.volume = resolve));
                    if (!receivedResponses.volumeMute) {
                        receivedResponses.volumeMute = new Promise(resolve => (markResponseReceived.volumeMute = resolve));
                    }
                }
            }, ['mute', 'volume']);
            const volumeHandlerId = this.socket.registerAsync(/Volume\s+of\s+LINE\s*:\s*(\d+)/i, (match) => {
                const value = parseInt(match[1], 10);
                if ('volume' in markResponseReceived) {
                    this.state.volumes.update('output', value);
                    markResponseReceived.volume();
                }
            });
            const volumeMuteHandlerId = this.socket.registerAsync(/^(Mute|Unmute)/i, (match) => {
                const value = match[1].toLowerCase() === 'mute';
                if ('volumeMute' in markResponseReceived) {
                    this.state.mutes.update('output', value);
                    markResponseReceived.volumeMute();
                }
            });
            const muteHandlerId = this.socket.registerAsync(/A\s*:\s*(\d+)\s*->\s*\d/i, (match) => {
                const channelNumber = parseInt(match[1], 10);
                for (const [key, channel] of Object.entries(this.channels)) {
                    if (channel.type.endsWith('Input') && key in markResponseReceived) {
                        this.state.mutes.update(key, channel.channelNumber !== channelNumber);
                        markResponseReceived[key]();
                    }
                }
            });
            try {
                yield this.socket.sendAsync(`600%`, ['onConfigure']);
                yield Promise.all(Object.values(receivedResponses));
            }
            finally {
                this.socket.unregisterAsync(muteHandlerId);
                this.socket.unregisterAsync(volumeMuteHandlerId);
                this.socket.unregisterAsync(volumeHandlerId);
            }
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
    // other class methods
    getKey(config) {
        for (const [key, channel] of Object.entries(this.channels)) {
            if (channel.channelNumber === config)
                return key;
        }
        this.log.error(`no channel for channel number: ${config}`);
        return '';
    }
    makeDetectedResults() {
        const detected = [];
        for (const channel of Object.values(this.channels)) {
            detected.push({
                channelNumber: channel.channelNumber,
                filters: this.makeFilters(channel.type),
                name: channel.name,
                type: channel.type,
            });
        }
        return detected;
    }
    makeFilters(type) {
        const filters = {};
        for (const [key, types] of Object.entries(allowedTypes)) {
            filters[key] = types.includes(type);
        }
        return filters;
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            const channel = this.channels[key];
            if (channel === undefined) {
                this.log.error(`no channel for config: ${config}`);
                return 'missing';
            }
            if (!allowedTypes[info.filter].includes(channel.type))
                return 'incompatible';
            return 'ok';
        });
    }
}
// static properties
AlfatronAmp.channelsConfigName = 'alfatronControlChannels';
AlfatronAmp.driverType = 'socketDriver';
AlfatronAmp.key = '-NhbAIkd9iJI27JJfp7v';
AlfatronAmp.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r', '\n'],
    },
    sendTerminator: '\r',
};
AlfatronAmp.socketOptions = [
    Object.assign(Object.assign({}, AlfatronAmp.baseSocketOptions), { port: 4001 }),
    Object.assign(Object.assign({}, AlfatronAmp.baseSocketOptions), { port: 4999 }),
];
